import { INBOX_TYPES } from 'shared/mixins/inboxMixin';

const MESSAGE_VARIABLES_REGEX = /{{(.*?)}}/g;
export const replaceVariablesInMessage = ({ message, variables }) => {
  return message.replace(MESSAGE_VARIABLES_REGEX, (match, replace) => {
    return variables[replace.trim()]
      ? variables[replace.trim().toLowerCase()]
      : '';
  });
};

const skipCodeBlocks = str => str.replace(/```(?:.|\n)+?```/g, '');

export const getFirstName = ({ user }) => {
  return user?.name ? user.name.split(' ').shift() : '';
};

export const getLastName = ({ user }) => {
  if (user && user.name) {
    return user.name.split(' ').length > 1 ? user.name.split(' ').pop() : '';
  }
  return '';
};

/**
 * Función para obtener el nombre del contacto compartido en un mensaje de tipo "contact".
 * Si el mensaje comparte varios contactos (contacts[]), se usa el nombre del ÚLTIMO contacto;
 * en otro caso se cae a contentAttributes.contact_name. Aplica para WhatsApp Cloud y
 * EvolutionAPI (ambos usan la misma clave contact_name por entrada).
 */
export const getContactNameShared = contentAttributes => {
  const { contact_name: contactName, contacts } = contentAttributes || {};
  if (Array.isArray(contacts) && contacts.length) {
    return contacts[contacts.length - 1].contact_name || contactName;
  }
  return contactName;
};

/**
 * Canales de línea de WhatsApp (Evolution y Cloud/360Dialog): un mensaje sin sender
 * se atribuye al teléfono de la línea en lugar del label "Mensaje externo".
 */
export const isAWhatsAppLineChannel = channelType =>
  [INBOX_TYPES.EVOLUTION_API, INBOX_TYPES.WHATSAPP].includes(channelType);

/**
 * Un adjunto que el proveedor rechazó y se reenvió al contacto como enlace no se muestra:
 * el mensaje conserva el archivo (el enlace apunta a él) pero lo que llegó fue el texto con la URL.
 */
export const hasVisibleAttachments = message => {
  const { attachments, content_attributes: contentAttributes } = message || {};
  if (!attachments || attachments.length === 0) return false;
  return !contentAttributes?.attachment_sent_as_link;
};

export const getMessageVariables = ({ conversation }) => {
  const {
    meta: { assignee = {}, sender = {} },
    id,
  } = conversation;

  return {
    'contact.name': sender?.name,
    'contact.first_name': getFirstName({ user: sender }),
    'contact.last_name': getLastName({ user: sender }),
    'contact.email': sender?.email,
    'contact.phone': sender?.phone_number,
    'contact.id': sender?.id,
    'conversation.id': id,
    'agent.name': assignee?.name ? assignee?.name : '',
    'agent.first_name': getFirstName({ user: assignee }),
    'agent.last_name': getLastName({ user: assignee }),
    'agent.email': assignee?.email ?? '',
  };
};

export const getUndefinedVariablesInMessage = ({ message, variables }) => {
  const messageWithOutCodeBlocks = skipCodeBlocks(message);
  const matches = messageWithOutCodeBlocks.match(MESSAGE_VARIABLES_REGEX);
  if (!matches) return [];

  return matches
    .map(match => {
      return match
        .replace('{{', '')
        .replace('}}', '')
        .trim();
    })
    .filter(variable => {
      return !variables[variable];
    });
};

// Función para eliminar el caracter \ que se pueda incluir en los textos cuando el usuario presiona ENTER o la combinación SHIFT + ENTER al escribir un mensaje
export const getTextWithOutBackslashes = text => {
  return text.replace(/\\/g, '');
};

// Función para sustituir el formato de texto utilizado en WhatsApp por Markdown, se sustituyen los caracteres especiales usados para el formato de negritas, itálicas y tachado. Esta función se usa para mostrar de forma correcta el texto enviado desde el Bot a los canales de WhatsApp.
export const convertTextFromWhatsAppFormattingToMarkdown = text => {
  return (
    text
      .replace(/\*_(?! )(.*?)(?<! )_\*/g, '\x01$1\x01') // *_texto_* → placeholder negrita+itálica
      .replace(/_\*(?! )(.*?)(?<! )\*_/g, '\x01$1\x01') // _*texto*_ → placeholder negrita+itálica
      .replace(/(?<!\*)\*(?!\*)(?! )(.*?)(?<! )(?<!\*)\*(?!\*)/g, '**$1**') // *texto* → **texto** (negrita WA → MD)
      .replace(/(?<!_)_(?!_)(?! )(.*?)(?<! )(?<!_)_(?!_)/g, '*$1*') // _texto_ → *texto* (itálica WA → MD)
      .replace(/(?<!~)~(?!~)(.*?)(?<!~)~(?!~)/g, '~~$1~~') // ~texto~ → ~~texto~~ (tachado WA → MD)
      // eslint-disable-next-line no-control-regex
      .replace(/\x01(.*?)\x01/g, '***$1***')
  ); // restaurar negrita+itálica → ***texto***
};
